import React, { FC, useEffect, useMemo, useState,useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { TooltipComponent } from "../../../components/Component";
import { Button } from "reactstrap";
import { Badge, DatePicker, Drawer, Progress, Table, Tag, Tooltip, Space, Button as AntButton, Dropdown, message, Segmented, Pagination, Empty, Skeleton } from "antd";
import type { MenuProps } from "antd";
import {
  CloseCircleOutlined,
  SearchOutlined,
  EyeOutlined,
  FileTextOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  InboxOutlined,
  ArrowLeftOutlined,
  FilterOutlined,
  PlusOutlined,
  ReloadOutlined,
  BarsOutlined,
  TableOutlined,
} from "@ant-design/icons";
import SearchInput from "../../form-components/SearchComponent";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import FormSelect from "../../form-components/SelectComponent";
import { useDispatch, useSelector } from "react-redux";
import { activityApis } from "../../../api/Activity";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import TopMenu from "../../../route/topMenu";
import { RootState } from "../../../store/store";
import ProjectPageShell from "../shared/ProjectPageShell";
import { GenericStatusPill } from "../shared/statusColors";
import WbsCodeToggle from "../shared/WbsCodeToggle";
import { useWbsName, useWbsRecordName } from "../shared/useWbsCodes";
import { getDirtyFieldsOnly } from "../../../utils/use-form/GetDirtyFieldsOnly";
import { projectsApis } from "../../../api/projects";
import { phaseApis } from "../../../api/Phase";
import { AddAccess, ViewAccess } from "../../../components/accessControl/AccessControl";
import Swal from "sweetalert2";
import ActivityQuickViewCard from "./ActivityQuickViewCard";
import DocumentsTabWrapper from "../projectDocument/DocumentsTabWrapper";
import { updateActivityInDetailModalSlice } from "../../../store/slices/projectManagement/activity-view/ActivityInDetailView";
import ActivityFieldsView from "./ActivityFieldsView";

const { RangePicker } = DatePicker;

const ACTIVITY_VIEW_STORAGE_KEY = "pms.activities.viewMode";

const initialsOf = (name?: string) => {
  if (!name) return "—";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};

const fmtListDate = (d?: string) => (d ? dayjs(d).format("DD MMM YYYY") : "—");

const ProjectActivityListPage: FC = () => {
  const dispatch = useDispatch();
  const { data: SideNavCollapse } = useSelector(
    (state: any) => state.SideNavCollapseStatus
  );
  const { data: RootPageRenderState } = useSelector(
    (state: RootState) => state.RootPageRenderState
  );
  const { data: currentUser } = useSelector((state: any) => state.currentUser);

  const location = useLocation();
  const navigate = useNavigate();
  const { id: stateProjectId, projectName: stateProjectName, extra, filterPhaseId, filterPhaseName, derivedProjectStatus } = location.state || {};
  const { screenName } = extra || {};

  // Get projectId from Redux layout state as fallback
  const { currentProjectId, currentProjectName } = useSelector(
    (state: RootState) => state.layout
  );

  // Use Redux projectId as fallback when location state doesn't have it
  const projectId = stateProjectId || currentProjectId;
  const projectName = stateProjectName || currentProjectName;

  const [filtersModal, setFiltersModal] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<any>();
  const [ownerFilterData, setOwnerFilterData] = useState<{ label: string; value: any }[]>([]);
  const [statusFilterData, setStatusFilterData] = useState<{ label: string; value: any }[]>([]);
  const [tagFilterData, setTagFilterData] = useState<{ label: string; value: any }[]>([]);
  const [phaseFilterData, setPhaseFilterData] = useState<{ label: string; value: any }[]>([]);
  const [filteredData, setFilteredData] = useState<any>({});
  const [activePhaseFilter, setActivePhaseFilter] = useState<string | null>(null);

  // Drawer state for activity quick actions
  const [actionDrawer, setActionDrawer] = useState<{
    open: boolean;
    type: "view" | "documents" | null;
    activity: any;
  }>({
    open: false,
    type: null,
    activity: null,
  });
  const [drawerViewMode, setDrawerViewMode] = useState<"view" | "edit">("view");
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Calculate allowEdit based on project status
  const projectStatus = derivedProjectStatus?.toLowerCase() || "";
  const allowEdit = !["closed", "onhold", "archived", "deleted"].includes(projectStatus);

  // allowAdd for Add Activity button - computed from API response (project status, lock)
  const [allowAdd, setAllowAdd] = useState(true);
  // Preloaded phases (with planned dates) so the Add Activity button can
  // pre-resolve a single applied phase before navigating to the create page.
  const [phaseSelectorOptions, setPhaseSelectorOptions] = useState<
    {
      label: string;
      value: number;
      phaseStartDate?: string;
      phaseEndDate?: string;
    }[]
  >([]);

  const openActionDrawer = (
    type: "view" | "documents",
    activity: any
  ) => {
    // Update Redux state for child components that need activity context
    dispatch(
      updateActivityInDetailModalSlice({
        open: true,
        id: activity.activityId,
        activityName: activity.activityName,
        projectResponse: {
          id: projectId,
          name: projectName,
          status: "Active",
        },
        phaseResponse: {
          id: activity.phaseId,
          name: activity.phaseName,
        },
        extra: {
          status: activity.status,
          startDate: activity.activityStartDate,
          endDate: activity.activityEndDate,
          ownerName: activity.activityOwnerName,
          ownerId: activity.activityOwnerId,
        },
      })
    );
    setDrawerViewMode("view");
    setActionDrawer({ open: true, type, activity });
  };

  const closeActionDrawer = () => {
    setActionDrawer({ open: false, type: null, activity: null });
    setDrawerViewMode("view");
    // Reset the Redux modal state to prevent it from opening on other pages
    dispatch(
      updateActivityInDetailModalSlice({
        open: false,
        id: null,
        activityName: "",
        projectResponse: {},
        phaseResponse: {},
        extra: {},
      })
    );
  };

  // Check if current activity can be edited
  const canEditCurrentActivity = () => {
    const activity = actionDrawer.activity;
    if (!activity || !allowEdit) return false;
    const status = activity.status?.toLowerCase();
    if (["closed", "archived", "deleted", "onhold", "cancelled"].includes(status)) return false;
    return true;
  };

  // Check if current activity can be archived
  const canArchiveCurrentActivity = () => {
    const activity = actionDrawer.activity;
    if (!activity || !allowEdit) return false;
    const status = activity.status?.toLowerCase();
    if (["archived", "deleted"].includes(status)) return false;
    return true;
  };

  // Check if current activity can be deleted
  const canDeleteCurrentActivity = () => {
    const activity = actionDrawer.activity;
    if (!activity || !allowEdit) return false;
    const status = activity.status?.toLowerCase();
    if (status === "deleted") return false;
    return true;
  };

  const handleArchiveActivity = async () => {
    const activity = actionDrawer.activity;
    if (!activity) return;

    Swal.fire({
      title: "Are you sure?",
      text: "You want to archive this activity?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, archive it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setActionLoading("archive");
        try {
          const response = await activityApis.archiveActivity(Number(activity.activityId));
          if (response?.status) {
            message.success("Activity archived successfully");
            refreshListView();
            closeActionDrawer();
          } else {
            message.error(response?.message || "Failed to archive activity");
          }
        } catch (error) {
          message.error("Failed to archive activity");
        } finally {
          setActionLoading(null);
        }
      }
    });
  };

  const handleDeleteActivity = async () => {
    const activity = actionDrawer.activity;
    if (!activity) return;

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setActionLoading("delete");
        try {
          const response = await activityApis.deleteActivity(Number(activity.activityId));
          if (response?.status) {
            message.success("Activity deleted successfully");
            refreshListView();
            closeActionDrawer();
          } else {
            message.error(response?.message || "Failed to delete activity");
          }
        } catch (error) {
          message.error("Failed to delete activity");
        } finally {
          setActionLoading(null);
        }
      }
    });
  };

  // Hamburger menu items for view drawer
  const getDrawerMenuItems = (): MenuProps["items"] => {
    const items: MenuProps["items"] = [];

    if (canEditCurrentActivity()) {
      items.push({
        key: "edit",
        icon: <EditOutlined />,
        label: "Edit",
        onClick: () => {
          setDrawerViewMode("edit");
        },
      });
    }

    if (canArchiveCurrentActivity()) {
      items.push({
        key: "archive",
        icon: <InboxOutlined />,
        label: "Archive",
        onClick: handleArchiveActivity,
      });
    }

    if (canDeleteCurrentActivity()) {
      items.push({
        key: "delete",
        icon: <DeleteOutlined />,
        label: "Delete",
        danger: true,
        onClick: handleDeleteActivity,
      });
    }

    return items;
  };

  const getDrawerTitle = () => {
    const { type, activity } = actionDrawer;
    if (!activity) return "";
    const prefix = activity.phaseName ? `${wbsName(activity.phaseName)} / ` : "";
    const name = activityLabel(activity);
    switch (type) {
      case "view":
        return `${prefix}${name} - Details`;
      case "documents":
        return `${prefix}${name} - Documents`;
      default:
        return `${prefix}${name}`;
    }
  };

  const getDrawerContent = () => {
    const { type, activity } = actionDrawer;

    if (type === "view") {
      if (drawerViewMode === "edit") {
        return (
          <ActivityFieldsView
            activityId={activity?.activityId}
            embedded={true}
            initialMode="edit"
            onModeChange={(mode: string) => {
              if (mode === "view") {
                setDrawerViewMode("view");
              }
            }}
            onClose={closeActionDrawer}
            onRefresh={refreshListView}
          />
        );
      }
      return (
        <ActivityQuickViewCard
          activityId={activity?.activityId}
          derivedProjectStatus={screenName}
          onClose={closeActionDrawer}
          onRefresh={refreshListView}
        />
      );
    }

    switch (type) {
      case "documents":
        return <DocumentsTabWrapper linkType="activity" parentDrawerMode={true} />;
      default:
        return null;
    }
  };

  const renderActionButtons = (record: any) => (
    <Space size={8}>
      <Tooltip title="View Details">
        <AntButton
          type="text"
          className="pms-action-btn"
          icon={<EyeOutlined />}
          onClick={() => openActionDrawer("view", record)}
        />
      </Tooltip>
      <Tooltip title="Documents">
        <AntButton
          type="text"
          className="pms-action-btn"
          icon={<FileTextOutlined />}
          onClick={() => openActionDrawer("documents", record)}
        />
      </Tooltip>
    </Space>
  );

  const columns: any = [
    {
      title: "Activity",
      dataIndex: "activityName",
      key: "activityName",
      width: 275,
      fixed: "left",
      render(text: any, record: any) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              id={`activity-${record?.activityId}`}
              onClick={() => {
                // Navigate to Tasks page with activity filter
                navigate(`/pms/project/tasks/${projectId}`, {
                  state: {
                    ...location.state,
                    projectId,
                    projectName,
                    filterActivityId: record.activityId,
                    filterActivityName: text,
                    filterPhaseId: record.phaseId,
                    filterPhaseName: record.phaseName,
                  },
                });
              }}
            >
              {activityLabel(record)}
            </span>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`activity-${record?.activityId}`}
              direction="right"
              text={"Click to view " + activityLabel(record) + " tasks"}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: "Phase",
      dataIndex: "phaseName",
      key: "phaseName",
      width: 200,
      render: (text: any, record: any) => (
        <span
          className="inlineTableCellLink"
          onClick={() => {
            // Navigate back to the Phases list page and pass the phase id/name
            // so PhasesListView can highlight that row.
            navigate(`/pms/project/phases/${projectId}`, {
              state: {
                ...location.state,
                projectId,
                projectName,
                filterPhaseId: record.phaseId,
                filterPhaseName: text,
              },
            });
          }}
        >
          {wbsName(text) || "N/A"}
        </span>
      ),
    },
    {
      title: "Planned Start Date",
      dataIndex: "activityStartDate",
      key: "activityStartDate",
      width: 200,
      render: (text: any) =>
        text ? dayjs(text)?.format("DD-MMM-YYYY") : "N/A",
    },
    {
      title: "Planned End Date",
      dataIndex: "activityEndDate",
      key: "activityEndDate",
      width: 200,
      render: (text: any) =>
        text ? dayjs(text)?.format("DD-MMM-YYYY") : "N/A",
    },
    {
      title: "Owner",
      dataIndex: "activityOwnerName",
      key: "activityOwnerName",
      width: 200,
    },
    {
      title: "Total Tasks",
      dataIndex: "totalTasks",
      key: "totalTasks",
      width: 150,
    },
    {
      title: "Completed Tasks",
      dataIndex: "completedTasks",
      key: "completedTasks",
      width: 150,
    },
    {
      title: "Tasks Completion %",
      dataIndex: "tasksCompletionPercentage",
      key: "tasksCompletionPercentage",
      width: 220,
      render: (data: any) => {
        const percent = Number(data);
        if (data === null || data === undefined || isNaN(percent)) return "N/A";
        return (
          <Tooltip title={`${Math.round(percent)}% Complete`} placement="top">
            <Progress
              strokeLinecap="butt"
              size={[186, 10]}
              percent={percent}
              status="active"
              strokeColor={{
                from: "#87d068",
                to: "#87d068",
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Budget Progress",
      dataIndex: "budgetConsumption",
      key: "budgetConsumption",
      width: 200,
      render: (data: any, record: any) => {
        const consumed = Number(data) || 0;
        const planned = Number(record?.budgetPlanned) || 0;
        const fmt = (n: number) =>
          `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
        if (planned <= 0) return consumed > 0 ? fmt(consumed) : "N/A";
        const percent = Math.round((consumed / planned) * 100);
        return (
          <Tooltip
            title={`${fmt(consumed)} of ${fmt(planned)} (${percent}%)`}
            placement="top"
          >
            <Progress
              strokeLinecap="butt"
              size={[186, 10]}
              percent={Math.min(100, percent)}
              status={percent > 100 ? "exception" : "active"}
              strokeColor={
                percent > 100
                  ? "#ff4d4f"
                  : { from: "#1677ff", to: "#1677ff" }
              }
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 200,
      render: (text: any) =>
        text ? dayjs(text)?.format("DD-MMM-YYYY") : "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (status: string) => <GenericStatusPill status={status} />,
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      fixed: "right",
      render: (_: any, record: any) => renderActionButtons(record),
    },
  ];

  const methods = useForm({
    mode: "onChange",
  });
  const {
    formState: { errors, dirtyFields },
  } = methods;

  const [dataSource, setDataSource] = useState<any>([]);
  // Activity/phase names embed a WBS code; the toolbar toggle switches between
  // the trimmed label and the raw stored name.
  const activityLabel = useWbsRecordName(
    dataSource,
    "activityId",
    "activityName"
  );
  const wbsName = useWbsName();
  // Option labels are formatted at render so they re-read the toggle without a
  // refetch (the fetch handlers keep the raw name).
  const phaseFilterOptions = useMemo(
    () =>
      phaseFilterData.map((option: any) => ({
        ...option,
        label: wbsName(option?.label),
      })),
    [phaseFilterData, wbsName]
  );
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    defaultCurrent: 1,
    defaultPageSize: 15,
    showSizeChanger: true,
    pageSizeOptions: ["15", "20", "30", "40", "50"],
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [searchQuery, setsearchQuery] = useState(null);
  const [filterCount, setFilterCount] = useState(0);
  const [endOfList, setEndOfList] = useState(false);
  const [viewMode, setViewMode] = useState<"table" | "list">(
    () =>
      (localStorage.getItem(ACTIVITY_VIEW_STORAGE_KEY) as "table" | "list") ||
      "list"
  );

  const fetchData = async (params: any, append = false) => {
    setLoading(true);
    try {
      const {
        sortOrder,
        pageSize,
        direction,
        pageDiff,
        paginationId,
        searchQuery,
      } = params;

      const apiResponse: any = await activityApis.fetchActivityList({
        page: params.current,
        pageSize: pageSize,
        searchQuery,
        direction,
        sortOrder,
        pageDiff,
        paginationId,
        projectIds: projectId?.toString(),
        // No phaseIds - fetch all activities for the project
        phaseIds: filteredData?.phase?.join(",") || null,
        tagIds: filteredData?.tag?.join(",") || null,
        activityStatus: filteredData?.status || null,
        activityOwnerIds: filteredData?.resourceHead?.join(",") || null,
        startDate:
          (customDateRange && dayjs(customDateRange[0]).format("YYYY-MM-DD")) ||
          null,
        endDate:
          (customDateRange && dayjs(customDateRange[1]).format("YYYY-MM-DD")) ||
          null,
        completionPercentage: filteredData?.completion || null,
        derivedProjectStatus: screenName,
      });

      const { data, status } = apiResponse || {};
      if (status) {
        const { list, count, projectResponse } = data || {};

        // Compute allowAdd based on project status and lock (same as Phases page)
        if (projectResponse) {
          let _allowAdd = true;
          const { status: projStatus, isLocked, lockedBy } = projectResponse;
          if (["closed", "onhold", "archived"]?.includes(projStatus?.toLowerCase())) {
            _allowAdd = false;
          }
          if (isLocked && lockedBy != currentUser?.id) {
            _allowAdd = false;
          }
          setAllowAdd(_allowAdd);
        } else if (projectId) {
          // Fallback: fetch project details if list API doesn't return projectResponse
          try {
            const projRes = await projectsApis.getProjectById(projectId?.toString(), screenName);
            if (projRes?.status && projRes?.data) {
              const { status: projStatus, isLocked, lockedBy } = projRes.data;
              let _allowAdd = true;
              if (["closed", "onhold", "archived"]?.includes(projStatus?.toLowerCase())) _allowAdd = false;
              if (isLocked && lockedBy != currentUser?.id) _allowAdd = false;
              setAllowAdd(_allowAdd);
            }
          } catch (_) {}
        }

        if (list?.length < pageSize) {
          setEndOfList(true);
        }
        setPagination((prev) => ({
          ...prev,
          total: Number(count),
        }));
        if (list)
          setDataSource((prev: any) => (append ? [...prev, ...list] : list));
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams: any = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
    };

    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize != pagination.pageSize) {
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage =
        Math.floor(
          ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
            Number(newPagination.pageSize)
        ) + 1;
      apiParams.current = _currentPage;
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current != pagination.current) {
        apiParams.direction =
          newPagination.current > pagination.current ? "F" : "B";
        apiParams.paginationId =
          apiParams.direction == "F"
            ? dataSource[dataSource.length - 1]?.uid ||
              dataSource[dataSource.length - 1]?.activityId
            : dataSource[0]?.uid || dataSource[0]?.activityId;

        apiParams.pageDiff =
          Math.abs(newPagination.current - pagination.current) - 1;
      }
    }

    setPagination({
      ...pagination,
      current: apiParams.current,
      pageSize: apiParams.pageSize,
    });
    await fetchData({ ...apiParams });
  };

  const refreshListView = () => {
    try {
      setEndOfList(false);
      setPagination({
        current: 1,
        pageSize: 15,
        defaultCurrent: 1,
        defaultPageSize: 15,
        showSizeChanger: true,
        pageSizeOptions: ["15", "20", "30", "40", "50"],
        total: 0,
      });
      refreshData({ pageDiff: 0 });
    } catch (error) {}
  };

  useEffect(() => {
    // Initial load or when root page render state changes.
    // If a phase filter is coming from navigation, let that handler
    // trigger the fetch instead to avoid duplicate API calls.
    if (!filterPhaseId && Object?.keys(filteredData)?.length === 0) {
      refreshListView();
    }
  }, [RootPageRenderState, filterPhaseId, filteredData]);

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => {
          return {
            ...item,
            linkState: location.state,
          };
        })
      )
    );
  }, [location]);

  // Cleanup: Reset modal state when component unmounts or navigates away
  useEffect(() => {
    return () => {
      dispatch(
        updateActivityInDetailModalSlice({
          open: false,
          id: null,
          activityName: "",
          projectResponse: {},
          phaseResponse: {},
          extra: {},
        })
      );
    };
  }, []);

  const fetchTagFilterData = async () => {
    try {
      const response = await activityApis.getTagsFilterData(
        projectId?.toString(),
        null // No phaseId
      );
      const { data, status } = response || {};
      if (status) {
        const options = data.map((item: any) => ({
          label: item.tagName,
          value: item.tagId,
        }));
        setTagFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const fetchOwnerFilterData = async () => {
    try {
      const response = await activityApis.getOwnersFilterData(
        projectId?.toString(),
        null // No phaseId
      );
      const { data, status } = response || {};
      if (status) {
        const options = data.map((item: any) => ({
          label: item.userName,
          value: item.userId,
        }));
        setOwnerFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const fetchStatusFilterData = async () => {
    try {
      const response = await projectsApis.fetchStatusFiltersByModule("Activity");
      const { data, status } = response;
      if (status) {
        const options = data.map((item: any) => ({
          label: item,
          value: item,
        }));
        setStatusFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const fetchPhaseFilterData = async () => {
    try {
      const response = await phaseApis.fetchPhaseDropDown({ projectId });
      const { data, status } = response;
      if (status && data) {
        const options = data.map((item: any) => ({
          label: item.phaseName,
          value: item.phaseId,
        }));
        setPhaseFilterData(options);
      }
    } catch (error) {
      console.error("Error loading phase filter data:", error);
    }
  };

  // Fetch phases for Add Activity phase selector. Pull phaseStartDate /
  // phaseEndDate too so the Activity Creation drawer can use them to
  // constrain its Planned Start / End date pickers (mirrors the
  // task-creation flow where activity dates constrain task dates).
  // Preloaded on projectId change so the Add Activity button can resolve
  // phase info from a single applied phase filter without first opening
  // the phase selector modal.
  useEffect(() => {
    if (projectId) {
      phaseApis.fetchPhaseDropDown({ projectId }).then((res) => {
        if (res?.status && res?.data) {
          setPhaseSelectorOptions(
            res.data.map((item: any) => ({
              label: item.phaseName,
              value: item.phaseId,
              phaseStartDate: item.phaseStartDate,
              phaseEndDate: item.phaseEndDate,
            }))
          );
        }
      });
    }
  }, [projectId]);

  useEffect(() => {
    if (filtersModal) {
      fetchTagFilterData();
      fetchOwnerFilterData();
      fetchStatusFilterData();
      fetchPhaseFilterData();
      // Pre-populate form with current filter values
      if (filterPhaseId && filteredData?.phase?.includes(filterPhaseId)) {
        methods.setValue("phase", [filterPhaseId]);
      }
    }
  }, [filtersModal]);

  // Apply phase filter when coming from Phases list and then
  // immediately clear it from location state so that side-menu
  // navigation to Activities shows the full list.
  useEffect(() => {
    if (filterPhaseId && filterPhaseName) {
      setFilteredData({ phase: [filterPhaseId] });
      setActivePhaseFilter(filterPhaseName);
      setFilterCount(1);

      // Clear the phase filter from navigation state (one-time use)
      navigate(location.pathname, {
        state: {
          ...location.state,
          filterPhaseId: undefined,
          filterPhaseName: undefined,
        },
        replace: true,
      });
    }
  }, [filterPhaseId, filterPhaseName, navigate, location.pathname, location.state]);

  // Refresh on every filter change (including clearing the phase chip back to {}).
  // Skip the initial mount because RootPageRenderState effect already fires fetchData.
  const isInitialFilterRunRef = useRef(true);
  useEffect(() => {
    if (isInitialFilterRunRef.current) {
      isInitialFilterRunRef.current = false;
      return;
    }
    refreshListView();
  }, [filteredData]);

  const refreshData = (overRideData?: any) => {
    fetchData({
      oldPage: 1,
      current: pagination.current.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize.toString(),
      direction: "P",
      pageDiff: pagination.current - 1,
      paginationId: null,
      searchQuery,
      dateRange: "Quarter to Date",
      ...overRideData,
    });
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  const onListPageChange = (page: number, pageSize: number) => {
    tableOnChange({ current: page, pageSize }, {}, {});
  };

  const goToPhase = (record: any) => {
    if (!record?.phaseId) return;
    navigate(`/pms/project/phases/${projectId}`, {
      state: {
        ...location.state,
        projectId,
        projectName,
        filterPhaseId: record.phaseId,
        filterPhaseName: record.phaseName,
      },
    });
  };

  const goToTasks = (record: any) => {
    navigate(`/pms/project/tasks/${projectId}`, {
      state: {
        ...location.state,
        projectId,
        projectName,
        filterActivityId: record.activityId,
        filterActivityName: record.activityName,
        filterPhaseId: record.phaseId,
        filterPhaseName: record.phaseName,
      },
    });
  };

  const renderActivityList = () => {
    if (loading) {
      return (
        <div className="pms-list-rows">
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="pms-list-row" style={{ cursor: "default" }}>
              <Skeleton active paragraph={false} title={{ width: "80%" }} />
            </div>
          ))}
        </div>
      );
    }

    if (!dataSource?.length) {
      return (
        <div className="pms-hub-empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No activities"
          />
        </div>
      );
    }

    return (
      <>
        <div className="pms-list-rows pms-view-fade">
          {dataSource.map((record: any) => {
            const pct = Number(record?.tasksCompletionPercentage);
            const tasksPct = isNaN(pct) ? 0 : pct;
            return (
              <div key={record.uid} className="pms-list-row activity-list-row">
                <div className="pms-row-main">
                  {record?.phaseName && (
                    <div
                      className="pms-eyebrow pms-row-link"
                      title={`Go to phase ${record.phaseName}`}
                      onClick={() => goToPhase(record)}
                    >
                      {wbsName(record.phaseName)}
                    </div>
                  )}
                  <div
                    className="pms-row-title inlineTableCellLink"
                    title={record.activityName}
                    onClick={() => goToTasks(record)}
                  >
                    {activityLabel(record)}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <GenericStatusPill status={record.status} />
                  </div>
                </div>

                <div className="pms-row-people">
                  <span className="pms-avatar">
                    {initialsOf(record.activityOwnerName)}
                  </span>
                  <span className="pms-meta-text">
                    {record.activityOwnerName || "Unassigned"}
                  </span>
                </div>

                <div className="pms-row-progress">
                  <Tooltip
                    title={`Closed: ${record.completedTasks ?? 0}, Total: ${
                      record.totalTasks ?? 0
                    }`}
                    placement="top"
                  >
                    <Progress
                      strokeLinecap="butt"
                      size={[160, 8]}
                      percent={tasksPct}
                      status="active"
                      strokeColor={{ from: "#87d068", to: "#87d068" }}
                    />
                  </Tooltip>
                  <div
                    className="pms-figure-caption"
                    style={{
                      marginTop: 6,
                      textTransform: "none",
                      letterSpacing: 0,
                    }}
                  >
                    {String(record.completedTasks ?? 0)}/
                    {String(record.totalTasks ?? 0)} tasks
                  </div>
                </div>

                <div className="pms-row-dates">
                  <div>{fmtListDate(record.activityStartDate)}</div>
                  <div>{fmtListDate(record.activityEndDate)}</div>
                </div>

                <div
                  className="phase-row-actions"
                  onClick={(e) => e.stopPropagation()}
                >
                  {renderActionButtons(record)}
                </div>
              </div>
            );
          })}
        </div>
        <div className="pms-hub-pagination">
          <Pagination
            size="small"
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger={pagination.showSizeChanger}
            pageSizeOptions={pagination.pageSizeOptions}
            onChange={onListPageChange}
            showTotal={(t: number) => `Total ${t} activities`}
          />
        </div>
      </>
    );
  };

  const clearPhaseFilterTag = activePhaseFilter ? (
    <Tag
      color="blue"
      closable
      onClose={() => {
        setActivePhaseFilter(null);
        setFilteredData({});
        methods.reset();
        setFilterCount(0);
        navigate(location.pathname, {
          state: {
            ...location.state,
            filterPhaseId: undefined,
            filterPhaseName: undefined,
          },
          replace: true,
        });
      }}
      style={{
        fontSize: 12,
        padding: "2px 8px",
        display: "flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      Clear Filter
    </Tag>
  ) : undefined;

  const toolbar = (
    <>
      <FormProvider {...methods}>
        <form>
          <div style={{ width: "300px" }}>
            <SearchInput
              name="search"
              placeholder="Search..."
              width="100%"
              prefixIcon={<SearchOutlined />}
              onDebouncedSearch={async (value: any) => {
                setsearchQuery(value);
                setPagination({
                  ...pagination,
                  current: 1,
                });
                await fetchData({
                  oldPage: 1,
                  current: 1,
                  sortOrder: "DESC",
                  pageSize: pagination.pageSize.toString(),
                  direction: "P",
                  pageDiff: 0,
                  paginationId: null,
                  searchQuery: value,
                  customDateRange,
                });
              }}
              debounceDelay={300}
            />
          </div>
        </form>
      </FormProvider>
      <span className="pms-toolbar-spacer" />
      <WbsCodeToggle />
      <AddAccess moduleName="Activities">
        <AntButton
          type="primary"
          icon={<PlusOutlined />}
          disabled={!allowAdd}
          onClick={() => {
            // Navigate to the full-page Add Activity form. Pre-resolve a phase
            // (from an explicit location-state filter, or a single phase
            // selected in the filter drawer) so the page opens pre-selected;
            // otherwise the page prompts for a phase itself.
            let resolvedPhaseId: number | null = null;
            let resolvedPhaseName: string | null = null;

            const appliedPhase = filteredData?.phase;
            if (
              filterPhaseId &&
              filterPhaseName &&
              (!Array.isArray(appliedPhase) ||
                appliedPhase.length === 0 ||
                appliedPhase.includes(filterPhaseId))
            ) {
              resolvedPhaseId = filterPhaseId;
              resolvedPhaseName = filterPhaseName;
            } else if (
              Array.isArray(appliedPhase) &&
              appliedPhase.length === 1
            ) {
              const opt = phaseSelectorOptions.find(
                (o) => o.value === appliedPhase[0]
              );
              if (opt) {
                resolvedPhaseId = opt.value;
                resolvedPhaseName = opt.label;
              }
            }

            navigate(`/pms/project/activity-create/${projectId}`, {
              state: {
                id: projectId,
                projectName,
                ...(resolvedPhaseId
                  ? {
                      filterPhaseId: resolvedPhaseId,
                      filterPhaseName: resolvedPhaseName,
                    }
                  : {}),
              },
            });
          }}
        >
          Add Activity
        </AntButton>
      </AddAccess>
      <ViewAccess moduleName="Activities">
        <AntButton
          type="text"
          icon={<ReloadOutlined />}
          title="Refresh"
          onClick={() => refreshListView()}
        />
      </ViewAccess>
      <Badge color="geekblue" size="small" count={filterCount}>
        <AntButton
          type="text"
          icon={<FilterOutlined />}
          onClick={() => setFiltersModal(true)}
        />
      </Badge>
      <Segmented
        value={viewMode}
        onChange={(v) => {
          const value = String(v) as "table" | "list";
          localStorage.setItem(ACTIVITY_VIEW_STORAGE_KEY, value);
          setViewMode(value);
        }}
        options={[
          { value: "table", icon: <TableOutlined />, title: "Table" },
          { value: "list", icon: <BarsOutlined />, title: "List" },
        ]}
      />
    </>
  );

  const breadcrumb = [
    { label: projectName },
    ...(activePhaseFilter ? [{ label: wbsName(activePhaseFilter) }] : []),
    { label: "Activities" },
  ];

  return (
    <>
      <ProjectPageShell
        headTitle="Activities"
        breadcrumb={breadcrumb}
        onBack={() => navigate(-1)}
        actions={clearPhaseFilterTag}
        toolbar={toolbar}
      >
        {viewMode === "table" ? (
          <div className="pms-table-panel">
            <SkeletonTable
              loading={loading}
              columns={columns as SkeletonTableColumnsType[]}
            >
              <Table
                rowKey="uid"
                className="pms-hub-table"
                columns={columns}
                dataSource={dataSource}
                pagination={pagination}
                onChange={tableOnChange}
                size="middle"
                scroll={{ x: 1400 }}
              />
            </SkeletonTable>
          </div>
        ) : (
          renderActivityList()
        )}
      </ProjectPageShell>

      <Drawer
        maskClosable={false}
        title={
          <div className="d-flex justify-content-between align-items-center">
            <strong>Filters</strong>
            <>
              <div style={{ textAlign: "right" }}>
                <Button
                  onClick={() => {
                    methods.reset();
                    setFilterCount(0);
                    setFilteredData({});
                    setCustomDateRange(null);
                    setActivePhaseFilter(null);
                    // Navigate to clear the location state
                    navigate(location.pathname, {
                      state: {
                        ...location.state,
                        filterPhaseId: undefined,
                        filterPhaseName: undefined,
                      },
                      replace: true,
                    });
                    refreshListView();
                  }}
                  color=""
                  className="m-3 mt-0 mb-0 ps-4 pe-4"
                  size="sm"
                >
                  <span style={{ color: "#0772f8" }}>Reset</span>
                </Button>
              </div>
            </>
          </div>
        }
        placement="right"
        width={450}
        onClose={() => {
          setFiltersModal(false);
        }}
        open={filtersModal}
        styles={{ body: { paddingBottom: 80 } }}
      >
        <div className="d-flex align-items-center flex-wrap w-100">
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit((data) => {
                setFilteredData(data);
                setFiltersModal(false);
                setFilterCount(0);
                const dirtyData = getDirtyFieldsOnly(data, dirtyFields);
                let count =
                  dirtyData !== undefined ? Object.keys(dirtyData).length : 0;
                if (
                  customDateRange &&
                  customDateRange[0] &&
                  customDateRange[1]
                ) {
                  count += 1;
                }
                setFilterCount(count);
                // Update activePhaseFilter based on selected phases
                if (data?.phase?.length > 0) {
                  const selectedPhase = phaseFilterData.find(
                    (p: any) => p.value === data.phase[0]
                  );
                  setActivePhaseFilter(selectedPhase?.label || null);
                } else {
                  setActivePhaseFilter(null);
                }
                if (
                  count == 0 &&
                  typeof data == "object" &&
                  Object.keys(data).length == 0
                )
                  refreshListView();
              })}
              className="w-100"
            >
              <div key={"filters-phase"} className="mt-0 mb-2">
                <FormMultiSelect
                  name={`phase`}
                  label={"Phase"}
                  placeholder={"Filter by Phase"}
                  options={phaseFilterOptions}
                />
              </div>

              <div key={"filters5"} className="mt-0 mb-2">
                <label className="form-label">Select Date Range</label>
                <RangePicker
                  name={`dateRange`}
                  value={
                    customDateRange
                      ? [
                          dayjs(customDateRange[0].toDate()),
                          dayjs(customDateRange[1].toDate()),
                        ]
                      : null
                  }
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      setCustomDateRange([dates[0], dates[1]]);
                    } else {
                      setCustomDateRange(null);
                    }
                  }}
                  style={{ width: "100%" }}
                />
              </div>
              <div key={"filters1"} className="mb-2 mt-0">
                <FormMultiSelect
                  name={`resourceHead`}
                  label={"Owner"}
                  placeholder={"Filter by Owner"}
                  options={ownerFilterData}
                />
              </div>

              <div key={"filters2"} className="mt-0 mb-2">
                <FormMultiSelect
                  name={`tag`}
                  label={"Tag"}
                  placeholder={"Filter by Tag"}
                  options={tagFilterData}
                />
              </div>

              <div key={"filters3"} className="mt-0 mb-2">
                <FormSelect
                  name={`completion`}
                  label={"Completion %"}
                  placeholder={"By Completion %"}
                  options={Array.from({ length: 20 }, (_, i) => {
                    const start = i * 5 + 1;
                    const end = (i + 1) * 5;
                    return {
                      value: `${start}-${end}`,
                      label: `${start}–${end}%`,
                    };
                  })}
                />
              </div>

              <div key={"filters4"} className="mt-0 mb-2">
                <FormSelect
                  name={`status`}
                  label={"Status"}
                  placeholder={"Filter by Status"}
                  options={statusFilterData}
                />
              </div>

              <div className="actions clearfix">
                <ul>
                  <li>
                    <Button color="primary" size="sm" type="submit">
                      Find
                    </Button>
                  </li>
                  <li>
                    <Button
                      size="sm"
                      color="primary"
                      className="btn-dim btn btn-secondary"
                      type="button"
                      onClick={() => {
                        methods.reset();
                        setFiltersModal(false);
                        setFilteredData({});
                        setFilterCount(0);
                        setCustomDateRange(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </li>
                </ul>
              </div>
            </form>
          </FormProvider>
        </div>
      </Drawer>

      {/* Action Drawer for View/Documents - PhaseCreation-style only in edit mode */}
      <Drawer
        maskClosable={actionDrawer.type === "view" && drawerViewMode === "edit" ? false : true}
        title={
          <div className="d-flex justify-content-between align-items-center">
            <strong>
              {actionDrawer.type === "documents" && <FileTextOutlined style={{ color: "#2557d6", marginRight: 8 }} />}
              {getDrawerTitle()}
            </strong>
            <>
              <div style={{ textAlign: "right" }}>
                {/* Back to View button when in edit mode */}
                {actionDrawer.type === "view" && drawerViewMode === "edit" && (
                  <AntButton
                    icon={<ArrowLeftOutlined />}
                    onClick={() => setDrawerViewMode("view")}
                    style={{
                      borderRadius: 6,
                      background: "#f5f5f5",
                      border: "1px solid #d9d9d9",
                      color: "#333",
                      marginRight: 8,
                    }}
                  >
                    Back to View
                  </AntButton>
                )}
                {/* Hamburger menu for view type */}
                {actionDrawer.type === "view" && drawerViewMode === "view" && (getDrawerMenuItems()?.length ?? 0) > 0 && (
                  <Dropdown
                    menu={{ items: getDrawerMenuItems() }}
                    trigger={["click"]}
                    placement="bottomRight"
                  >
                    <AntButton
                      type="text"
                      icon={<MoreOutlined style={{ fontSize: 18 }} />}
                      style={{
                        borderRadius: 6,
                        padding: "4px 8px",
                      }}
                    />
                  </Dropdown>
                )}
              </div>
            </>
          </div>
        }
        placement="right"
        width={actionDrawer.type === "view" && drawerViewMode === "edit" ? 1200 : 720}
        onClose={closeActionDrawer}
        open={actionDrawer.open}
        styles={actionDrawer.type === "view" && drawerViewMode === "edit" ? { body: { paddingBottom: 80 } } : undefined}
        destroyOnClose
      >
        {actionDrawer.open && getDrawerContent()}
      </Drawer>
    </>
  );
};

export default ProjectActivityListPage;
